import { useMemo, useState } from 'preact/hooks';
import { lessonsOnDay, quickNoteContext } from '../../lib/quick-note.ts';
import { slotTimeLabel } from '../../lib/slots.ts';
import { isAmbiguous, matchStudentInText } from '../../lib/student-match.ts';
import { SUBJECTS } from '../../lib/subjects.ts';
import { studentDisplayName } from '../../lib/student-roster.ts';
import { live, useStore } from '../../store/store.ts';
import { closeModal, openModal, toast } from '../../ui/ui-store.ts';

/**
 * One line in, one filed observation out.
 *
 * The whole point is that nothing has to be chosen: the teacher types
 * "{élève} {ce qu'il faut retenir}", and the pupil, the date, the lesson and
 * the subject are all read off that line plus the clock. Everything derived
 * is still shown and still overridable — a note going into the wrong child's
 * file is the one outcome worth a second of friction to avoid.
 */
export function QuickNoteModal() {
  const students = live(useStore((s) => s.students));
  const lessons = live(useStore((s) => s.lessons));

  // Frozen at the moment the dialog opened. The lesson "en cours" is the one
  // running when the observation was made, not when the teacher got round to
  // pressing Enter.
  const now = useMemo(() => new Date(), []);
  const context = useMemo(() => quickNoteContext(lessons, now), [lessons, now]);

  const [raw, setRaw] = useState('');
  const [date, setDate] = useState(context.date);
  const [lessonOverride, setLessonOverride] = useState<string | null>(null);
  const [subjectOverride, setSubjectOverride] = useState<string | null>(null);

  /**
   * A hand-picked pupil, remembered together with the word it was picked for.
   *
   * Tied to the first word rather than to the whole text, so carrying on
   * typing the note keeps the choice, while starting the line over with a
   * different name drops it — clearing it on every keystroke made the pick
   * impossible to keep, and never clearing it filed the note on whoever was
   * named in a sentence the teacher had since replaced.
   */
  const [pick, setPick] = useState<{ studentId: string; lead: string } | null>(null);

  const matches = useMemo(() => matchStudentInText(raw, students), [raw, students]);

  const lead = raw.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  const studentOverride = pick && pick.lead === lead ? pick.studentId : null;
  const setStudentOverride = (studentId: string) => setPick({ studentId, lead });

  const studentId = studentOverride ?? matches[0]?.studentId ?? '';
  const student = students.find((s) => s.id === studentId) ?? null;

  // The split belonging to the chosen pupil, so picking a runner-up whose name
  // was read differently ("Léa" vs "Léa Dupont") also fixes where the note
  // starts. Falls back to the best split when the pupil came from the list.
  const split = matches.find((m) => m.studentId === studentId) ?? matches[0] ?? null;
  const note = (split?.rest ?? raw).trim();

  // The timetable of the date in the field, not of the day the dialog opened
  // on: a note written up after the fact belongs to that day's lessons. The
  // clock only says which lesson is running when the date is still today.
  const isToday = date === context.date;
  const dayLessons = isToday ? context.dayLessons : lessonsOnDay(lessons, date);
  const suggested = isToday ? context.current : null;

  const lessonId = lessonOverride ?? suggested?.id ?? '';
  const lesson = dayLessons.find((l) => l.id === lessonId) ?? null;
  const subject = subjectOverride ?? lesson?.subject ?? '';

  const ambiguous = studentOverride === null && isAmbiguous(matches);

  function save() {
    if (!studentId) {
      toast('Commence la note par le nom de l’élève');
      return;
    }
    if (!note) {
      toast('Écris ce que tu veux retenir');
      return;
    }

    useStore.getState().addObservation(
      { studentId, date, lessonId, subject, difficulty: '', note, support: '' },
      null,
    );
    closeModal();
    toast(`Note rangée dans le dossier de ${student ? studentDisplayName(student.name) : 'l’élève'}`);
  }

  if (!students.length) {
    return (
      <div class="empty">
        Ajoute d’abord des élèves : la note rapide reconnaît le prénom écrit au début.
      </div>
    );
  }

  return (
    <>
      <label for="qnText">Ta note</label>
      <textarea
        id="qnText"
        value={raw}
        autofocus
        rows={3}
        placeholder="Léa ne lit pas la consigne avant de commencer"
        lang="fr"
        spellcheck={true}
        autocorrect="on"
        autocapitalize="sentences"
        aria-describedby="qnHint"
        onInput={(e) => setRaw(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) save();
        }}
      />
      <p id="qnHint" class="small muted" style={{ margin: '6px 0 0' }}>
        Commence par le prénom de l’élève : «&nbsp;Prénom&nbsp;» puis ce que tu veux retenir.
      </p>

      <div class="detailSection" style={{ marginTop: '14px' }}>
        <div class="row">
          <div>
            <label style={{ margin: 0 }}>Élève</label>
            <div class="small muted">
              {student
                ? ambiguous
                  ? 'Plusieurs élèves correspondent — vérifie.'
                  : `Reconnu dans « ${split?.matched ?? ''} »`
                : 'Aucun élève reconnu pour l’instant.'}
            </div>
          </div>
        </div>

        {matches.length > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
            {matches.map((m) => {
              const candidate = students.find((s) => s.id === m.studentId);
              return (
                <button
                  key={m.studentId}
                  class={m.studentId === studentId ? 'btn primary' : 'btn soft'}
                  aria-pressed={m.studentId === studentId}
                  onClick={() => setStudentOverride(m.studentId)}
                >
                  {candidate ? studentDisplayName(candidate.name) : 'Élève'}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: '10px' }}>
          <label for="qnStudent">Corriger l’élève</label>
          <select
            id="qnStudent"
            value={studentId}
            onChange={(e) => setStudentOverride(e.currentTarget.value)}
          >
            <option value="">Choisir un élève</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {studentDisplayName(s.name)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div class="formGrid" style={{ marginTop: '14px' }}>
        <div>
          <label for="qnDate">Date</label>
          <input
            id="qnDate"
            type="date"
            value={date}
            onInput={(e) => {
              setDate(e.currentTarget.value);
              // A lesson picked on the previous date is not one of this date's.
              setLessonOverride(null);
            }}
          />
        </div>

        <div>
          <label for="qnLesson">{isToday ? 'Leçon en cours' : 'Leçon'}</label>
          <select
            id="qnLesson"
            value={lessonId}
            onChange={(e) => {
              setLessonOverride(e.currentTarget.value);
              // The subject follows the lesson again unless it was set by hand.
              setSubjectOverride(null);
            }}
          >
            <option value="">Hors leçon</option>
            {dayLessons.map((l) => (
              <option key={l.id} value={l.id}>
                {slotTimeLabel(l.slot)} · {l.title}
              </option>
            ))}
          </select>
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="qnSubject">Matière</label>
          <select
            id="qnSubject"
            value={subject}
            onChange={(e) => setSubjectOverride(e.currentTarget.value)}
          >
            <option value="">Aucune matière</option>
            {SUBJECTS.map(([name]) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {note && (
        <p class="small muted" style={{ marginTop: '12px' }}>
          Sera enregistré&nbsp;: «&nbsp;{note}&nbsp;»
        </p>
      )}

      <div style={{ marginTop: '15px' }}>
        <button class="btn primary" onClick={save}>
          Ranger dans le dossier de l’élève
        </button>
      </div>
    </>
  );
}

export function openQuickNote(): void {
  openModal('Note rapide', <QuickNoteModal />);
}
